import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { PlusIcon } from "@heroicons/react/20/solid";
import { Button } from "@/components/button";
import { Input, InputGroup } from "@/components/input";
import { MagnifyingGlassIcon, FunnelIcon } from "@heroicons/react/16/solid";
import { PaginationComponent } from "@/components/PaginationComponent";
import { useExamPaperList } from "@/apis/exam_papers/getExamPaperList";
import { getPaginationInfo } from "@/types/pagination";
import type { IExamPaper } from "@/apis/exam_papers/types";
import { Badge } from "@/components/badge";
import { getPaperTypeLabel } from "@/constants/exam";
import { getDifficultyLabel } from "@/utils/exam";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/table";
import SortableTableHeader from "@/components/SortableTableHeader";
import ActiveFilters, { type Filter } from "@/components/ActiveFilters";
import FilteringExamPaperListModal, {
  type FilterState,
} from "./components/FilteringExamPaperListModal";

const EXAM_PAPER_LIST_SETTINGS_KEY = "examPaperListSettings";

type SortField = "title" | "difficulty" | "created_at" | null;
type SortOrder = "asc" | "desc";

interface ExamPaperListSettings {
  filters: FilterState;
  sortField: SortField;
  sortOrder: SortOrder;
}

const defaultSettings: ExamPaperListSettings = {
  filters: {
    typeFilter: "all",
    difficultyFilter: "all",
  },
  sortField: null,
  sortOrder: "desc",
};

const ExamPaperListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  // Load initial settings from localStorage once
  const initialSettings = useState(() => {
    try {
      const savedSettings = localStorage.getItem(EXAM_PAPER_LIST_SETTINGS_KEY);
      if (savedSettings) {
        const parsedSettings: ExamPaperListSettings = JSON.parse(savedSettings);
        return parsedSettings;
      }
    } catch (error) {
      console.error("Failed to load settings from localStorage:", error);
    }
    return defaultSettings;
  })[0];

  // Filter state with loaded initial values
  const [filters, setFilters] = useState<FilterState>(initialSettings.filters);

  // Sorting state with loaded initial values
  const [sortField, setSortField] = useState<SortField>(
    initialSettings.sortField
  );
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    initialSettings.sortOrder
  );

  const [searchParams, setSearchParams] = useState({
    page: 1,
    limit: 10,
  });

  // Save settings to localStorage when they change
  useEffect(() => {
    try {
      const settings: ExamPaperListSettings = {
        filters,
        sortField,
        sortOrder,
      };
      localStorage.setItem(
        EXAM_PAPER_LIST_SETTINGS_KEY,
        JSON.stringify(settings)
      );
    } catch (error) {
      console.error("Failed to save settings to localStorage:", error);
    }
  }, [filters, sortField, sortOrder]);

  // Update search params when filters or sorting change
  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      subject: filters.typeFilter === "all" ? undefined : filters.typeFilter,
      difficulty:
        filters.difficultyFilter === "all"
          ? undefined
          : parseInt(filters.difficultyFilter),
      sort: sortField || undefined,
      sort_order: sortField ? sortOrder : undefined,
      page: 1, // Reset to first page when filters or sorting change
    }));
  }, [filters, sortField, sortOrder]);

  // API 호출
  const { data, isLoading, error } = useExamPaperList(searchParams);

  // 검색 처리
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams((prev) => ({
      ...prev,
      title: searchTerm,
      page: 1, // 검색 시 첫 페이지로 이동
    }));
  };

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      if (sortOrder === "asc") {
        // asc → desc
        setSortOrder("desc");
      } else {
        // desc → 정렬 해제
        setSortField(null);
      }
    } else {
      // New field clicked, set to ascending
      setSortField(field);
      setSortOrder("asc");
    }
  };

  // Filter modal handlers
  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
  };

  const handleRemoveFilter = (filterKey: string) => {
    if (filterKey === "typeFilter") {
      setFilters((prev) => ({
        ...prev,
        typeFilter: "all",
      }));
    } else if (filterKey === "difficultyFilter") {
      setFilters((prev) => ({
        ...prev,
        difficultyFilter: "all",
      }));
    }
  };

  const handleClearAllFilters = () => {
    setFilters({
      typeFilter: "all",
      difficultyFilter: "all",
    });
  };

  // Get active filters for display
  const getActiveFilters = (): Filter[] => {
    const activeFilters: Filter[] = [];

    if (filters.typeFilter !== "all") {
      const typeLabel =
        filters.typeFilter === "reading_writing" ? "Reading Writing" : "Math";
      activeFilters.push({
        key: "typeFilter",
        label: `Type: ${typeLabel}`,
        value: filters.typeFilter,
      });
    }

    if (filters.difficultyFilter !== "all") {
      activeFilters.push({
        key: "difficultyFilter",
        label: `Difficulty: Level ${filters.difficultyFilter}`,
        value: filters.difficultyFilter,
      });
    }

    return activeFilters;
  };

  // 페이지네이션 정보
  const paginationInfo = data ? getPaginationInfo(data) : null;

  // 페이지 이동 핸들러
  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  // 시험지 추가 페이지로 이동
  const handleAddExamPaper = () => {
    navigate("/exam_papers/create");
  };

  // 시험지 상세 페이지로 이동
  const handleExamPaperClick = (id: string) => {
    navigate(`/exam_papers/${id}`);
  };

  // 시험지 미리보기 페이지로 이동
  const handlePreviewClick = (id: string, event: React.MouseEvent) => {
    event.stopPropagation(); // 행 클릭 이벤트 방지
    navigate(`/exam_papers/${id}/preview/1`); // 첫 번째 모듈로 이동
  };

  // Get status display
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "active":
        return <Badge color="lime">Active</Badge>;
      case "inactive":
        return <Badge color="zinc">Inactive</Badge>;
      case "draft":
        return <Badge color="yellow">Draft</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Format date display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <Heading>Exam Papers</Heading>
        <Text className="text-zinc-500">
          View all exam papers. Use the search to find specific papers.
        </Text>

        <div className="flex justify-between mt-4">
          <div className="flex items-center gap-3">
            <Button onClick={handleAddExamPaper}>
              <PlusIcon className="h-5 w-5" />
              Add Exam Paper
            </Button>

            <Button onClick={handleOpenFilterModal}>
              <FunnelIcon />
              Filter
            </Button>
          </div>

          <form onSubmit={handleSearch} className="flex items-center">
            <InputGroup>
              <MagnifyingGlassIcon className="h-5 w-5 text-zinc-400" />
              <Input
                name="search"
                placeholder="Search by title..."
                aria-label="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Button type="submit" className="ml-2">
              Search
            </Button>
          </form>
        </div>

        {/* Active Filters */}
        <ActiveFilters
          filters={getActiveFilters()}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />

        {/* Loading State */}
        {isLoading && (
          <div className="text-center py-8">
            <Text>Loading exam papers...</Text>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-8 text-red-600">
            <Text>Failed to load exam papers. Please try again.</Text>
          </div>
        )}

        {/* Exam Papers Table */}
        {!isLoading && !error && (
          <Table>
            <TableHead>
              <TableRow>
                <SortableTableHeader
                  sortKey="title"
                  currentSortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                >
                  Title
                </SortableTableHeader>
                <TableHeader>Exam Type</TableHeader>
                <TableHeader>Subject</TableHeader>
                <SortableTableHeader
                  sortKey="difficulty"
                  currentSortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                >
                  Difficulty
                </SortableTableHeader>
                <TableHeader>Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.items && data.items.length > 0 ? (
                data.items.map((examPaper) => (
                  <TableRow
                    key={examPaper.id}
                    onClick={() => handleExamPaperClick(examPaper.id)}
                    className="cursor-pointer hover:bg-zinc-50"
                  >
                    <TableCell className="font-medium">
                      {examPaper.title}
                    </TableCell>
                    <TableCell>{examPaper.exam_type}</TableCell>
                    <TableCell>
                      {getPaperTypeLabel(examPaper.subject)}
                    </TableCell>
                    <TableCell>
                      {getDifficultyLabel(examPaper.difficulty)}
                    </TableCell>
                    <TableCell>
                      <Button
                        onClick={(e: React.MouseEvent) =>
                          handlePreviewClick(examPaper.id, e)
                        }
                        color="white"
                      >
                        Preview
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-4">
                    No exam papers found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {paginationInfo && (
          <div className="mt-8 flex justify-center">
            <PaginationComponent
              currentPage={paginationInfo.currentPage}
              totalPages={paginationInfo.totalPages}
              hasNextPage={paginationInfo.hasNextPage}
              hasPrevPage={paginationInfo.hasPrevPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Filter Modal */}
        <FilteringExamPaperListModal
          open={isFilterModalOpen}
          onClose={handleCloseFilterModal}
          onApply={handleApplyFilters}
          currentFilters={filters}
        />
      </div>
    </PageContainer>
  );
};

export default ExamPaperListPage;
